import React, { useMemo } from 'react';
import { themeBackgroundIcons } from '@/utils/rewards';

const ThemeIcons = ({ isEnabled, themeId }) => {
  // Generate spaced positions and animations for icons
  const icons = useMemo(() => {
    const themeIcons = themeBackgroundIcons[themeId] || themeBackgroundIcons.mono;
    const iconCount = 10; // Number of floating icons
    
    // Create a staggered grid layout (5 columns x 2 rows, offset for better spacing)
    const cols = 5;
    const rows = 2;
    const cellWidth = 100 / cols;
    const cellHeight = 100 / rows;
    
    return Array.from({ length: iconCount }, (_, i) => {
      const icon = themeIcons[i % themeIcons.length];
      
      // Calculate grid position
      const col = i % cols;
      const row = Math.floor(i / cols);
      
      // Offset every other row for staggered look
      const rowOffset = row % 2 === 1 ? cellWidth / 2 : 0;
      
      // Center within cell with small random offset (keeps icons apart)
      const jitterX = (Math.random() - 0.5) * 8; // -4% to +4%
      const jitterY = (Math.random() - 0.5) * 10; // -5% to +5%
      
      const left = col * cellWidth + cellWidth / 2 + rowOffset + jitterX;
      const top = row * cellHeight + cellHeight / 2 + jitterY;
      
      const size = 22 + Math.random() * 14; // 22px to 36px
      const opacity = 0.12 + Math.random() * 0.08; // 0.12 to 0.2
      const animationDuration = 20 + Math.random() * 20; // 20s to 40s
      const animationDelay = Math.random() * -15; // Stagger start times
      const rotation = Math.random() * 30 - 15; // -15deg to +15deg (subtle rotation)
      
      return {
        id: i,
        icon,
        style: {
          left: `${Math.min(95, Math.max(5, left))}%`, // Keep within 5-95% bounds
          top: `${Math.min(90, Math.max(10, top))}%`, // Keep within 10-90% bounds
          fontSize: `${size}px`,
          opacity,
          animationDuration: `${animationDuration}s`,
          animationDelay: `${animationDelay}s`,
          transform: `rotate(${rotation}deg)`,
        },
      };
    });
  }, [themeId]);

  if (!isEnabled) return null;

  return (
    <div 
      className="fixed inset-0 pointer-events-none overflow-hidden z-0"
      aria-hidden="true"
    >
      {icons.map(({ id, icon, style }) => (
        <span
          key={id}
          className="absolute animate-float-icon select-none"
          style={style}
        >
          {icon}
        </span>
      ))}
      
      <style>{`
        @keyframes float-icon {
          0%, 100% {
            transform: translateY(0px) rotate(var(--rotation, 0deg));
          }
          25% {
            transform: translateY(-15px) rotate(calc(var(--rotation, 0deg) + 5deg));
          }
          50% {
            transform: translateY(-5px) rotate(calc(var(--rotation, 0deg) - 3deg));
          }
          75% {
            transform: translateY(-20px) rotate(calc(var(--rotation, 0deg) + 8deg));
          }
        }
        
        .animate-float-icon {
          animation: float-icon ease-in-out infinite;
          --rotation: inherit;
        }
      `}</style>
    </div>
  );
};

export default ThemeIcons;
